import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Home, Calendar, Clock, FileText, DollarSign, Shield, CheckSquare, Timer, HelpCircle, LogOut, TrendingUp, BookOpen, Eye, UserPlus, IndianRupee, Wallet, LifeBuoy, HandCoins } from 'lucide-react';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PORTAL_KEY_TO_TAB = {
  attendance: 'attendance',
  leaves: 'leaves',
  holidays: 'holidays',
  policies: 'policies',
  payslips: 'payslips',
  compensation: 'compensation',
  overtime: 'overtime',
  training: 'training',
  selfhelp: 'selfhelp',
  exit: 'exit',
  performance: 'performance',
  approvals: 'approvals',
  'hr-portal': 'hr-portal',
  hiring: 'hiring',
  pnl: 'pnl',
  payroll: 'payroll',
};

const Navigation = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isRM, setIsRM] = useState(false);
  const [isHR, setIsHR] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [portalAccess, setPortalAccess] = useState(null);

  useEffect(() => {
    checkIfRM();
    checkIfHR();
    fetchPortalAccess();
  }, []);

  const checkIfRM = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/my-approvals/check-rm`, config);
      
      setIsRM(response.data.is_rm);
      
      if (response.data.is_rm) {
        const pendingResponse = await axios.get(`${API}/my-approvals/pending`, config);
        const totalPending = (pendingResponse.data.attendance?.length || 0) + 
                           (pendingResponse.data.leaves?.length || 0) + 
                           (pendingResponse.data.overtime?.length || 0);
        setPendingCount(totalPending);
      }
    } catch (error) {
      console.error('Error checking RM status:', error);
    }
  };

  const checkIfHR = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/hr-portal/check-access`, config);
      setIsHR(response.data.has_access);
    } catch (error) {
      console.error('Error checking HR access:', error);
    }
  };

  const fetchPortalAccess = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/portal-access/my/check`, config);
      setPortalAccess(response.data.portals || {});
    } catch (error) {
      console.error('Error fetching portal access:', error);
    }
  };

  const isPortalEnabled = (tabValue) => {
    if (user.role === 'admin' || user.role === 'superadmin') return true;
    if (!portalAccess) return true;
    const portalKey = Object.entries(PORTAL_KEY_TO_TAB).find(([, v]) => v === tabValue)?.[0];
    if (!portalKey) return true;
    return portalAccess[portalKey] !== false;
  };

  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/admin') return 'home';
    if (path === '/holidays') return 'holidays';
    if (path === '/attendance') return 'attendance';
    if (path === '/leaves') return 'leaves';
    if (path === '/overtime') return 'overtime';
    if (path === '/policies') return 'policies';
    if (path === '/payslips') return 'payslips';
    if (path === '/compensation') return 'compensation';
    if (path === '/my-approvals') return 'approvals';
    if (path === '/self-help') return 'selfhelp';
    if (path === '/exit-portal') return 'exit';
    if (path === '/help') return 'help';
    if (path === '/performance') return 'performance';
    if (path === '/training') return 'training';
    if (path === '/hr-portal') return 'hr-portal';
    if (path === '/hiring') return 'hiring';
    if (path === '/pnl') return 'pnl';
    if (path === '/payroll') return 'payroll';
    return 'home';
  };

  const baseTabs = user.role === 'admin' || user.role === 'superadmin' ? [
    { value: 'home', label: 'Admin', icon: Shield, path: '/admin' },
    { value: 'holidays', label: 'Holidays', icon: Calendar, path: '/holidays' },
    { value: 'selfhelp', label: 'Self Help', icon: HelpCircle, path: '/self-help' },
    { value: 'exit', label: 'Exit Portal', icon: LogOut, path: '/exit-portal' },
    { value: 'pnl', label: 'P&L', icon: IndianRupee, path: '/pnl' },
    { value: 'payroll', label: 'Payroll', icon: Wallet, path: '/payroll' },
    { value: 'compensation', label: 'Compensation', icon: HandCoins, path: '/compensation' },
    { value: 'help', label: 'Help', icon: LifeBuoy, path: '/help' },
  ] : [
    { value: 'home', label: 'Home', icon: Home, path: '/dashboard' },
    { value: 'holidays', label: 'Holidays', icon: Calendar, path: '/holidays' },
    { value: 'attendance', label: 'Attendance', icon: Clock, path: '/attendance' },
    { value: 'leaves', label: 'Leave', icon: FileText, path: '/leaves' },
    { value: 'overtime', label: 'Overtime', icon: Timer, path: '/overtime' },
    { value: 'policies', label: 'Policies', icon: Shield, path: '/policies' },
    { value: 'payslips', label: 'Payslips', icon: DollarSign, path: '/payslips' },
    { value: 'compensation', label: 'Compensation', icon: HandCoins, path: '/compensation' },
    { value: 'training', label: 'Training', icon: BookOpen, path: '/training' },
    { value: 'selfhelp', label: 'Self Help', icon: HelpCircle, path: '/self-help' },
    { value: 'exit', label: 'Exit Portal', icon: LogOut, path: '/exit-portal' },
    { value: 'performance', label: 'Performance', icon: TrendingUp, path: '/performance' },
    { value: 'help', label: 'Help', icon: LifeBuoy, path: '/help' },
  ];

  let tabs = [...baseTabs];

  // Admins/superadmins who are also reporting managers get the employee/manager portals too
  if ((user.role === 'admin' || user.role === 'superadmin') && isRM) {
    const existing = new Set(tabs.map(t => t.value));
    const managerTabs = [
      { value: 'attendance', label: 'Attendance', icon: Clock, path: '/attendance' },
      { value: 'leaves', label: 'Leave', icon: FileText, path: '/leaves' },
      { value: 'overtime', label: 'Overtime', icon: Timer, path: '/overtime' },
      { value: 'policies', label: 'Policies', icon: Shield, path: '/policies' },
      { value: 'payslips', label: 'Payslips', icon: DollarSign, path: '/payslips' },
      { value: 'training', label: 'Training', icon: BookOpen, path: '/training' },
      { value: 'performance', label: 'Performance', icon: TrendingUp, path: '/performance' },
    ].filter(t => !existing.has(t.value));
    tabs = [tabs[0], ...managerTabs, ...tabs.slice(1)];
  }

  if (isRM) {
    tabs.push({ value: 'approvals', label: 'My Approvals', icon: CheckSquare, path: '/my-approvals', badge: pendingCount });
  }
  
  if (isHR && user.role !== 'admin' && user.role !== 'superadmin') {
    tabs.push({ value: 'hr-portal', label: 'HR Portal', icon: Eye, path: '/hr-portal' });
    tabs.push({ value: 'hiring', label: 'Hiring', icon: UserPlus, path: '/hiring' });
  }

  const visibleTabs = tabs.filter(tab => {
    if (tab.value === 'home') return true;
    return isPortalEnabled(tab.value);
  });

  return (
    <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sticky top-[80px] z-40" data-testid="navigation">
      <Tabs value={getActiveTab()} className="w-full">
        <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 justify-start w-full">
          {visibleTabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              onClick={() => navigate(tab.path)}
              className="flex items-center gap-2 px-3 py-2 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 relative whitespace-nowrap"
              data-testid={`nav-tab-${tab.value}`}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.badge > 0 && (
                <Badge variant="destructive" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {tab.badge}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
};

export default Navigation;
